/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {
    FocusEventHandler,
    InputHTMLAttributes,
    KeyboardEventHandler,
    TextareaHTMLAttributes,
    useEffect,
    useRef,
    useState
} from 'react';
import {enterPressed, ErrorType, generateComponentId, OrnamentShape} from '@dvrd/dvr-controls';
import BolmateInput from "./bolmateInput";

interface Props {
    onChange: (value: string, evt: React.ChangeEvent) => void;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    onEnter?: KeyboardEventHandler;
    value: string | number;
    label?: string;
    ornaments?: OrnamentShape | OrnamentShape[];
    inputProps?: InputHTMLAttributes<any> & TextareaHTMLAttributes<any>;
    disabled?: boolean;
    autoFocus?: boolean;
    autoSelect?: boolean;
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    ornamentClassName?: string;
    errorClassName?: string;
    error?: ErrorType;
    id?: string;
    fullWidth?: boolean;
    active?: boolean;
    area?: boolean;
    unControlled?: boolean;
    allowCharReg?: string | RegExp;
    placeholder?: string;
}

export default function InputController(props: Props) {
    const {
        label, labelClassName, ornamentClassName, inputClassName, errorClassName, autoSelect, inputProps, className,
        id, area, active, autoFocus, onFocus, unControlled, error, fullWidth, onBlur, onChange, onKeyDown,
        disabled, ornaments, onEnter, allowCharReg, placeholder
    } = props;
    const [value, setValue] = useState(props.value);
    const componentId = useRef(generateComponentId(id));

    function _onChange(evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        if (!disabled) {
            const newValue = sanitizeValue(evt.target.value);
            setValue(newValue);
            onChange(newValue, evt);
        }
    }

    function _onKeyDown(evt: React.KeyboardEvent) {
        if (!evt.metaKey && !charIsAllowed(evt.key)) return evt.preventDefault();
        if (!disabled) {
            if (onEnter && enterPressed(evt)) onEnter(evt);
            else if (onKeyDown) onKeyDown(evt);
        }
    }

    function sanitizeValue(value: string) {
        if(!allowCharReg) return value;
        const reg: RegExp = typeof allowCharReg === 'string' ? new RegExp(allowCharReg) : allowCharReg;
        const chars: Array<string> = [];
        for(const char of value){
            if(reg.test(char)) chars.push(char);
        }
        return chars.join('');
    }

    function charIsAllowed(char: string): boolean {
        if (!allowCharReg) return true;
        const reg = typeof allowCharReg === 'string' ? new RegExp(allowCharReg) : allowCharReg;
        return reg.test(char);
    }

    function _onFocus(evt: React.FocusEvent) {
        if (!disabled && onFocus) onFocus(evt);
    }

    function _onBlur(evt: React.FocusEvent) {
        if (!disabled && onBlur) onBlur(evt);
    }

    useEffect(() => {
        if (!unControlled) setValue(props.value);
    }, [props.value]);

    return (
        <BolmateInput onChange={_onChange} onFocus={_onFocus} onKeyDown={_onKeyDown} value={value}
                      disabled={disabled} autoFocus={autoFocus} error={error} label={label} ornaments={ornaments}
                      inputProps={inputProps} className={className} inputClassName={inputClassName}
                      labelClassName={labelClassName} ornamentClassName={ornamentClassName} fullWidth={fullWidth}
                      errorClassName={errorClassName} id={componentId.current} onBlur={_onBlur} autoSelect={autoSelect}
                      active={active} area={area} placeholder={placeholder}/>
    );
}